import { useCallback, useEffect, useRef, useState } from 'react';
import { CardResult } from './cardResults';
import './cardResultsMobile.sass';

const SCROLL_TOLERANCE = 1;

function getCardKey(item, index) {
  return item.id || item.url || `${item.title || 'resultado'}-${index}`;
}

/** Renderiza las recomendaciones como un carrusel navegable en dispositivos móviles. */
export default function CardResultsMobile({ data = [], id, ariaLabel }) {
  const trackRef = useRef(null);
  const [canGoPrevious, setCanGoPrevious] = useState(false);
  const [canGoNext, setCanGoNext] = useState(false);

  const updateNavigation = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    setCanGoPrevious(track.scrollLeft > SCROLL_TOLERANCE);
    setCanGoNext(
      track.scrollLeft + track.clientWidth < track.scrollWidth - SCROLL_TOLERANCE
    );
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateNavigation);

    updateNavigation();
    track.addEventListener('scroll', updateNavigation, { passive: true });
    window.addEventListener('resize', updateNavigation);
    resizeObserver?.observe(track);

    return () => {
      track.removeEventListener('scroll', updateNavigation);
      window.removeEventListener('resize', updateNavigation);
      resizeObserver?.disconnect();
    };
  }, [data, updateNavigation]);

  const moveScroll = useCallback((direction) => {
    const track = trackRef.current;
    if (!track) return;

    const firstSlide = track.querySelector('.mobile-results-carousel__slide');
    const slideWidth = firstSlide?.getBoundingClientRect().width || track.clientWidth * 0.85;

    track.scrollBy({
      left: direction * slideWidth,
      behavior: 'smooth',
    });
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveScroll(-1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveScroll(1);
    }
  };

  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <div id={id} className="mobile-results-carousel" role="region" aria-label={ariaLabel}>
      {canGoPrevious && (
        <button
          type="button"
          className="mobile-results-carousel__button mobile-results-carousel__button--previous"
          onClick={() => moveScroll(-1)}
          aria-label="Ver recomendación anterior"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
      )}

      <div
        ref={trackRef}
        className="mobile-results-carousel__track"
        tabIndex="0"
        onKeyDown={handleKeyDown}
      >
        {data.map((item, index) => (
          <div className="mobile-results-carousel__slide" key={getCardKey(item, index)}>
            <CardResult item={item} />
          </div>
        ))}
      </div>

      {canGoNext && (
        <button
          type="button"
          className="mobile-results-carousel__button mobile-results-carousel__button--next"
          onClick={() => moveScroll(1)}
          aria-label="Ver siguiente recomendación"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      )}
    </div>
  );
}
